import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { Plus } from 'lucide-react';
// Import API services
import {
  folderService,
  FolderResponse,
} from '../../ai-blog-writer/services/folderApi';
import blogService, {
  allBlogResponse,
} from '../../ai-blog-writer/services/blogApi';
import { collabService } from '../../ai-blog-writer/services/collaboratorApi';
import publishService from '../../ai-blog-writer/services/publishApi';
import schedulingService from '../../ai-blog-writer/services/scheduleApi';

// Import components
import {
  ShareModal,
  ScheduleModal,
  NewFolderModal,
  BlogTabs,
  MyFilesTable,
  PublishedBlogsTable,
  SharedBlogsTable,
  getStatusColor,
  MobileFilesView,
} from '../components/blog-writer';
import { unified } from 'unified';
import remarkParse from 'remark-parse';
import remarkMdx from 'remark-mdx';
import remarkHtml from 'remark-html';

// Define the SelectedItem interface
interface SelectedItem {
  id: string | number;
  type: 'folder' | 'file';
  name: string;
}

const BlogWriterContent = () => {
  const [activeTab, setActiveTab] = useState('myfiles');
  const [expandedFolders, setExpandedFolders] = useState<{
    [key: string]: boolean;
  }>({});
  const [selectedItems, setSelectedItems] = useState<SelectedItem[]>([]);

  // API data states
  const [folders, setFolders] = useState<FolderResponse[]>([]);
  const [allBlogs, setAllBlogs] = useState<any[]>([]);
  const [sharedBlogs, setSharedBlogs] = useState<any[]>([]);
  const [publishedBlogs, setPublishedBlogs] = useState<allBlogResponse[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // Modal states
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false);
  const [isNewFolderModalOpen, setIsNewFolderModalOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [scheduledDate, setScheduledDate] = useState('');

  // Form states
  const [selectedFileId, setSelectedFileId] = useState<number | null>(null);
  const [shareEmail, setShareEmail] = useState('');

  const navigate = useNavigate();

  // Fetch folders and blogs on component mount
  useEffect(() => {
    fetchFolders();
    fetchAllBlogs();
    fetchSharedBlogs();
  }, []);

  // Fetch folders
  const fetchFolders = async () => {
    setIsLoading(true);
    try {
      const response = await folderService.getAllFolders();
      if (response.success && response.data) {
        setFolders(response.data);

        // Initialize expanded state for all folders
        const expandedState: { [key: string]: boolean } = {};
        response.data.forEach((folder) => {
          expandedState[folder.folder_name] = true;
        });
        setExpandedFolders(expandedState);
      } else {
        toast.error(response.message || 'Failed to fetch folders');
      }
    } catch (error) {
      console.error('Error fetching folders:', error);
      toast.error('Failed to fetch folders');
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch all blogs
  const fetchAllBlogs = async () => {
    setIsLoading(true);
    try {
      const response = await blogService.getAllBlogs();
      if (response.success && response.data) {
        setAllBlogs(response.data);

        // Filter published and scheduled blogs
        const publishedAndScheduled = response.data.filter(
          (blog) => blog.status === 'published' || blog.status === 'scheduled'
        );
        setPublishedBlogs(publishedAndScheduled);
      } else {
        toast.error(response.message || 'Failed to fetch blogs');
      }
    } catch (error) {
      console.error('Error fetching blogs:', error);
      toast.error('Failed to fetch blogs');
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch shared blogs
  const fetchSharedBlogs = async () => {
    setIsLoading(true);
    try {
      const response = await blogService.getSharedBlogs();
      if (response.success && response.data) {
        // Convert BlogResponse to allBlogResponse format
        const formattedSharedBlogs = await Promise.all(
          response.data.map(async (blog: any) => {
            const htmlString = await unified()
              .use(remarkParse)
              .use(remarkMdx)
              .use(remarkHtml)
              .process(blog.blog.context);
            return {
              id: blog.blog.id,
              title: blog.blog.title,
              file_name: blog.blog.file_name,
              topic: blog.blog?.topic || '',
              status: 'shared',
              content: htmlString.toString(),
              markdown: blog.blog.context,
              min_len: blog.blog.min_len,
              max_len: blog.blog.max_len,
              language: blog.blog.language,
              tone: blog.blog.tone,
              industry: blog.blog.industry,
              img_number: blog.blog.img_number,
              permission: blog.collaboration.permission_level,
              folder_id: parseInt(blog.blog.folder_id) || 0,
              created_at: new Date().toISOString(), // Use current date as fallback
              cover_img_url: blog.images_urls?.split(',')[0] || '',
            };
          })
        );
        console.log('formattedSharedBlogs', formattedSharedBlogs);

        setSharedBlogs(formattedSharedBlogs);
      } else {
        toast.error(response.message || 'Failed to fetch shared blogs');
      }
    } catch (error) {
      console.error('Error fetching shared blogs:', error);
      toast.error('Failed to fetch shared blogs');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle share file
  const handleShareFile = async () => {
    if (!shareEmail) {
      toast.error('Please enter an email address');
      return;
    }

    if (!selectedFileId) {
      toast.error('No file selected');
      return;
    }

    try {
      const response = await collabService.inviteCollaborator(
        selectedFileId.toString(),
        shareEmail
      );

      if (response.success) {
        toast.success(`Invitation sent to ${shareEmail}`);
        setShareEmail('');
        setIsShareModalOpen(false);
      } else {
        toast.error(response.message || 'Failed to share file');
      }
    } catch (error) {
      console.error('Error sharing file:', error);
      toast.error('Failed to share file');
    }
  };

  // Handle create file
  const handleCreateFile = () => {
    // Navigate to the blog creation page
    navigate('/ai-blog-writer/get-started');
  };

  // Handle download file
  const handleDownloadFile = async (blogId: number, fileName: string) => {
    try {
      // First, get the blog content
      const response = await blogService.getBlog(blogId);

      if (response.success && response.data) {
        // Convert markdown to HTML using unified with remark plugins
        const markdownContent =
          response.data.context || response.data.blog?.context;

        if (!markdownContent) {
          toast.error('No content found for this blog');
          return;
        }

        try {
          // Import required libraries
          const { unified } = await import('unified');
          const remarkParse = (await import('remark-parse')).default;
          const remarkHtml = (await import('remark-html')).default;

          // Convert markdown to HTML
          const htmlResult = await unified()
            .use(remarkParse)
            .use(remarkHtml, { sanitize: false })
            .process(markdownContent);

          const htmlContent = htmlResult.toString();

          // Create a new window with the HTML content
          const printWindow = window.open('', '_blank');
          if (!printWindow) {
            toast.error('Pop-up blocked. Please allow pop-ups and try again.');
            return;
          }

          // Add HTML content with styling
          printWindow.document.write(`
            <!DOCTYPE html>
            <html>
            <head>
              <title>${fileName || 'Blog'}</title>
              <style>
                body {
                  font-family: Arial, sans-serif;
                  line-height: 1.6;
                  margin: 2rem;
                  max-width: 800px;
                  margin: 0 auto;
                  padding: 2rem;
                }
                h1, h2, h3, h4, h5, h6 {
                  margin-top: 1.5rem;
                  margin-bottom: 1rem;
                  color: #333;
                }
                p {
                  margin-bottom: 1rem;
                }
                img {
                  max-width: 100%;
                  height: auto;
                  margin: 1rem 0;
                }
                blockquote {
                  border-left: 4px solid #ccc;
                  padding-left: 1rem;
                  font-style: italic;
                  color: #555;
                }
                pre, code {
                  background-color: #f5f5f5;
                  padding: 0.2rem 0.4rem;
                  border-radius: 3px;
                  font-family: monospace;
                }
                ul, ol {
                  margin-bottom: 1rem;
                  padding-left: 2rem;
                }
                @media print {
                  body {
                    margin: 1.5cm;
                  }
                }
              </style>
            </head>
            <body>
              <h1>${fileName || 'Blog'}</h1>
              ${htmlContent}
            </body>
            </html>
          `);

          // Trigger print dialog after content is loaded
          printWindow.document.close();
          printWindow.onload = function () {
            printWindow.focus();
            printWindow.print();
          };

          toast.success('Blog prepared for printing');
        } catch (conversionError) {
          console.error('Error converting markdown to HTML:', conversionError);
          toast.error('Failed to convert blog content');
        }
      } else {
        toast.error('Failed to fetch blog content');
      }
    } catch (error) {
      console.error('Error downloading file:', error);
      toast.error('Failed to download file');
    }
  };

  // Handle create folder
  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) {
      toast.error('Please enter a folder name');
      return;
    }

    try {
      const response = await folderService.createFolder(newFolderName);

      if (response.success && response.data) {
        toast.success('Folder created successfully');
        setNewFolderName('');
        setIsNewFolderModalOpen(false);
        // Refresh folders
        fetchFolders();
      } else {
        toast.error(response.message || 'Failed to create folder');
      }
    } catch (error) {
      console.error('Error creating folder:', error);
      toast.error('Failed to create folder');
    }
  };

  // Handle delete folder
  const handleDeleteFolder = async (folderId: number) => {
    try {
      const response = await folderService.deleteFolder(folderId);

      if (response.success) {
        toast.success('Folder deleted successfully');
        // Refresh folders
        fetchFolders();
      } else {
        toast.error(response.message || 'Failed to delete folder');
      }
    } catch (error) {
      console.error('Error deleting folder:', error);
      toast.error('Failed to delete folder');
    }
  };

  // Handle delete blog
  const handleDeleteBlog = async (blogId: number) => {
    try {
      const response = await blogService.deleteBlog(blogId);
      if (response.success) {
        toast.success('Blog deleted successfully');
        // Refresh blogs
        fetchAllBlogs();
      } else {
        toast.error(response.message || 'Failed to delete blog');
      }
    } catch (error) {
      console.error('Error deleting blog:', error);
      toast.error('Failed to delete blog');
    }
  };

  // Handle schedule blog
  const handleScheduleBlog = async () => {
    if (!selectedFileId) {
      toast.error('No file selected');
      return;
    }

    if (!scheduledDate) {
      toast.error('Please select a date');
      return;
    }

    try {
      const response = await schedulingService.scheduleBlog({
        blog_id: selectedFileId.toString(),
        scheduled_at: scheduledDate,
      });

      if (response.success) {
        toast.success('Blog scheduled successfully');
        setScheduledDate('');
        setIsScheduleModalOpen(false);
        // Refresh blogs
        fetchAllBlogs();
      } else {
        toast.error(response.message || 'Failed to schedule blog');
      }
    } catch (error) {
      console.error('Error scheduling blog:', error);
      toast.error('Failed to schedule blog');
    }
  };

  // Handle publish blog
  const handlePublishBlog = async (blogId: number) => {
    try {
      const response = await publishService.publishBlog(blogId);
      if (response.success) {
        toast.success('Blog published successfully');
        fetchAllBlogs();
      } else {
        toast.error(response.message || 'Failed to publish blog');
      }
    } catch (error) {
      console.error('Error publishing blog:', error);
      toast.error('Failed to publish blog');
    }
  };

  // Toggle folder expansion
  const toggleFolder = (folderName: string) => {
    setExpandedFolders({
      ...expandedFolders,
      [folderName]: !expandedFolders[folderName],
    });
  };

  // Format folders and blogs for display
  const myFilesData = {
    folders:
      folders.length > 0
        ? folders.map((folder) => ({
            id: folder.id.toString(),
            name: folder.folder_name,
            createdAt: folder.created_at,
            count: folder.blogs?.length || 0,
            files: allBlogs
              .filter((blog) => blog.folder_id === folder.id)
              .map((blog: any) => ({
                id: blog.id,
                name: blog.title,
                context: blog.context,
                dateCreated: new Date(blog.created_at).toLocaleDateString(
                  'en-US',
                  {
                    weekday: 'short',
                    day: '2-digit',
                    month: 'short',
                    year: '2-digit',
                  }
                ),
                viewers: ['A'], // Placeholder for viewers
              })),
          }))
        : [
            {
              id: 'default',
              name: 'Default',
              count: 0,
              files: [],
            },
          ],
  };

  const sharedBlogsData =
    sharedBlogs.length > 0
      ? sharedBlogs.map((blog: any) => ({
          id: blog.id,
          name: blog.file_name,
          context: blog.context,
          markdown: blog.markdown,
          settings: {
            minLength: blog.min_len,
            maxLength: blog.max_len,
            language: blog.language,
            toneOfVoice: blog.tone,
            industry: blog.industry,
            imageCount: blog.img_number,
            permission: blog.permission,
          },
          images: blog.blog_images,
          markDownContent: blog.markDownContent,
          wordCount: blog.wordCount,
          content: blog.content,
          dateAdded: new Date(blog.created_at).toLocaleDateString('en-US', {
            weekday: 'short',
            day: '2-digit',
            month: 'short',
            year: '2-digit',
          }),
          viewers: ['A'], // Placeholder for viewers
        }))
      : [];

  // Add this new function to handle folder updates
  const handleUpdateFolder = async (folderId: string, newName: string) => {
    if (!newName.trim()) {
      toast.error('Folder name cannot be empty');
      return;
    }

    try {
      const response = await folderService.updateFolder(
        parseInt(folderId),
        newName
      );

      if (response.success) {
        toast.success('Folder updated successfully');
        // Refresh folders
        fetchFolders();

        // Update expanded folders state with the new name
        const oldFolder = folders.find((f) => f.id.toString() === folderId);
        if (oldFolder) {
          const oldName = oldFolder.folder_name;
          setExpandedFolders((prev) => {
            const newState = { ...prev };
            if (oldName in newState) {
              const isExpanded = newState[oldName];
              delete newState[oldName];
              newState[newName] = isExpanded;
            }
            return newState;
          });
        }
      } else {
        toast.error(response.message || 'Failed to update folder');
      }
    } catch (error) {
      console.error('Error updating folder:', error);
      toast.error('Failed to update folder');
    }
  };

  // Add this helper function at the top of the component, after the state declarations
  const getFormattedDate = () => {
    return 'Wed, 27 Nov, 24';
  };

  // Handle bulk delete
  const handleBulkDelete = async () => {
    if (selectedItems.length === 0) return;

    const folderIds: number[] = [];
    const blogIds: number[] = [];

    // Separate folder and file IDs
    selectedItems.forEach((item) => {
      if (item.type === 'folder') {
        folderIds.push(parseInt(item.id.toString()));
      } else if (item.type === 'file') {
        blogIds.push(parseInt(item.id.toString()));
      }
    });

    let successCount = 0;
    let failCount = 0;

    // Delete folders
    for (const folderId of folderIds) {
      try {
        const response = await folderService.deleteFolder(folderId);
        if (response.success) {
          successCount++;
        } else {
          failCount++;
          console.error(
            `Failed to delete folder ${folderId}: ${response.message}`
          );
        }
      } catch (error) {
        failCount++;
        console.error(`Error deleting folder ${folderId}:`, error);
      }
    }

    // Delete blogs
    for (const blogId of blogIds) {
      try {
        const response = await blogService.deleteBlog(blogId);
        if (response.success) {
          successCount++;
        } else {
          failCount++;
          console.error(`Failed to delete blog ${blogId}: ${response.message}`);
        }
      } catch (error) {
        failCount++;
        console.error(`Error deleting blog ${blogId}:`, error);
      }
    }

    // Show appropriate toast messages
    if (successCount > 0) {
      toast.success(`Successfully deleted ${successCount} item(s)`);
    }

    if (failCount > 0) {
      toast.error(`Failed to delete ${failCount} item(s)`);
    }

    // Refresh data
    fetchFolders();
    fetchAllBlogs();

    // Clear selection
    setSelectedItems([]);
  };

  // Handle selection change
  const handleSelectionChange = (items: SelectedItem[]) => {
    setSelectedItems(items);
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-primary ">Your Blog</h1>
        <p className=" text-text-secondary">
          Manage your blog posts, drafts, and publications
        </p>
      </div>

      {/* Mobile View */}
      <MobileFilesView
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        folders={myFilesData.folders}
        expandedFolders={expandedFolders}
        toggleFolder={toggleFolder}
        sharedFiles={
          sharedBlogs.length === 0
            ? []
            : sharedBlogs.map((blog) => ({
                id: blog.id,
                name: blog.file_name || blog.title || 'Untitled',
                dateCreated: getFormattedDate(),
                viewers: [],
              }))
        }
        publishedFiles={publishedBlogs.map((blog) => ({
          id: blog.id,
          name: blog.file_name || blog.title || 'Untitled',
          dateCreated: getFormattedDate(),
          status: blog.status,
          version: 1,
          platform: 'LinkedIn',
          viewers: [],
        }))}
        handleDeleteFolder={handleDeleteFolder}
        handleDeleteBlog={handleDeleteBlog}
        handleDownloadFile={handleDownloadFile}
        onShare={(blogId) => {
          setSelectedFileId(blogId);
          setIsShareModalOpen(true);
        }}
        onEdit={(blogId) => navigate(`/ai-blog-writer/blog?id=${blogId}`)}
        handleUpdateFolder={handleUpdateFolder}
      />

      {/* Floating Action Button (Mobile) */}
      <button
        onClick={handleCreateFile}
        className="lg:hidden fixed bottom-6 right-6 w-14 h-14 bg-secondary text-primary rounded-full flex items-center justify-center shadow-lg z-10"
      >
        <Plus size={24} />
      </button>

      {/* Desktop View */}
      <div className="hidden lg:block">
        <BlogTabs
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          onNewFolder={() => setIsNewFolderModalOpen(true)}
          onNewFile={handleCreateFile}
          selectedItems={selectedItems}
          onBulkDelete={handleBulkDelete}
        />

        {/* Loading indicator */}
        {isLoading && (
          <div className="flex justify-center items-center py-10">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#846CEC]"></div>
          </div>
        )}

        {/* My Files Tab */}
        {activeTab === 'myfiles' && !isLoading && (
          <MyFilesTable
            folders={myFilesData.folders}
            expandedFolders={expandedFolders}
            toggleFolder={toggleFolder}
            handleDeleteFolder={handleDeleteFolder}
            handleDeleteBlog={handleDeleteBlog}
            handleDownloadFile={handleDownloadFile}
            onSchedule={(blogId) => {
              setSelectedFileId(blogId);
              setIsScheduleModalOpen(true);
            }}
            handleUpdateFolder={handleUpdateFolder}
            onSelectionChange={handleSelectionChange}
          />
        )}

        {/* Published and Scheduled Tab */}
        {activeTab === 'published' && !isLoading && (
          <PublishedBlogsTable
            blogs={publishedBlogs}
            handleDeleteBlog={handleDeleteBlog}
            handlePublishBlog={handlePublishBlog}
            getStatusColor={getStatusColor}
            handleDownloadFile={handleDownloadFile}
          />
        )}

        {/* Shared with me Tab */}
        {activeTab === 'shared' && !isLoading && (
          <SharedBlogsTable
            blogs={sharedBlogsData}
            handleDownloadFile={handleDownloadFile}
          />
        )}
      </div>

      {/* Modals */}
      <ShareModal
        isOpen={isShareModalOpen}
        onOpenChange={setIsShareModalOpen}
        shareEmail={shareEmail}
        setShareEmail={setShareEmail}
        handleShareFile={handleShareFile}
      />

      <ScheduleModal
        isOpen={isScheduleModalOpen}
        onOpenChange={setIsScheduleModalOpen}
        scheduledDate={scheduledDate}
        setScheduledDate={setScheduledDate}
        handleScheduleBlog={handleScheduleBlog}
      />

      <NewFolderModal
        isOpen={isNewFolderModalOpen}
        onOpenChange={setIsNewFolderModalOpen}
        newFolderName={newFolderName}
        setNewFolderName={setNewFolderName}
        handleCreateFolder={handleCreateFolder}
      />
    </div>
  );
};

export default BlogWriterContent;
